import React, { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { bool, func, node, string, shape, any } from "prop-types";
import { getConditionallyRequiredProp } from "@lib/getConditionallyRequiredProp";

import { useIsInitialRender } from "@hooks/useIsInitialRender";
import CloseIcon from "./Icons/Close";
import { useTranslation } from "@lib/i18n";
import { useRouter } from "next/router";
import { twJoin, twMerge } from "tailwind-merge";
import useGlobalScrollPrevent from "@hooks/useGlobalScrollPrevent";

const Modal = ({
  ariaLabel,
  children,
  className,
  disableBackdropClick = false,
  disableEscapePress = false,
  doNotRenderInvisible = false,
  closeButtonDataId,
  firstElement,
  isOpen,
  lastElement,
  onClose,
  preventGoBack = false,
  withCloseIcon,
  ...props
}) => {
  const isInitialRender = useIsInitialRender();
  const closeIconRef = useRef(null);
  const router = useRouter();
  const { t } = useTranslation();
  const [isClosing, setIsClosing] = useState(false);

  const controlKeys = {
    TAB: 9,
  };

  const handleClose = () => {
    if (isClosing) {
      return;
    }

    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 300);
  };

  useGlobalScrollPrevent(isOpen);

  // if CloseIcon is used - it will be the first element for tab navigation
  if (withCloseIcon) {
    firstElement = closeIconRef;
  }
  // if the last element is not set - the first element will be the last for tab navigation
  if (!lastElement) {
    lastElement = firstElement;
  }

  useEffect(() => {
    router.beforePopState(() => {
      if (isOpen && preventGoBack) {
        handleClose();
        window.history.pushState(null, document.title, window.location.href);
        return false;
      }
      return true;
    });

    return () => {
      router.beforePopState(() => true);
    };
  }, [isOpen, preventGoBack, router]);

  useEffect(() => {
    if (isOpen) {
      if (firstElement && firstElement.current) firstElement.current.focus();
      document.onkeydown = event => {
        if (event.keyCode === controlKeys.TAB && firstElement && lastElement) {
          if (event.target.tagName === "BODY") {
            // if focus has been lost - set it to firstElement again
            event.preventDefault();
            firstElement.current.focus();
          } else if (event.target === firstElement.current && event.shiftKey) {
            event.preventDefault();
            lastElement.current.focus();
          } else if (event.target === lastElement.current && !event.shiftKey) {
            event.preventDefault();
            firstElement.current.focus();
          }
        }
      };
    } else {
      document.onkeydown = null;
    }
    return () => (document.onkeydown = null);
  }, [isOpen, closeIconRef.current]);

  useEffect(() => {
    const listener = event => {
      if (event.key === "Escape" && !disableEscapePress && isOpen) {
        handleClose();
      }
    };

    if (!disableEscapePress && isOpen) {
      document.addEventListener("keydown", listener);
    }

    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, [disableEscapePress, isOpen, handleClose]);

  const onBackdropClick = () => {
    if (disableBackdropClick) {
      return;
    }

    handleClose();
  };

  // Modals are attached directly to the document using
  // portals, the document object is not available on the
  // server, so they can't be rendered during SSR
  if (isInitialRender) {
    return null;
  }

  if (doNotRenderInvisible && !isOpen) {
    return null;
  }

  return createPortal(
    <div
      className={twMerge(
        "justify-center items-center absolute inset-0",
        isOpen ? "flex" : "hidden"
      )}
    >
      <div
        className={twJoin(
          "max-sm:hidden bg-primaryDark/90 backdrop-blur-xs fixed inset-0 z-50",
          isClosing ? "animate-fadeOut" : "animate-fadeIn"
        )}
        onClick={onBackdropClick}
      />
      <div className="grid place-items-center fixed inset-0 z-50 pointer-events-none">
        <div
          aria-label={ariaLabel}
          aria-modal="true"
          className={twMerge(
            "relative bg-white border border-primaryDark size-full max-w-full px-1 py-4 pointer-events-auto sm:rounded-xl sm:h-auto sm:w-160",
            "border-2 border-primaryLight bg-gradient-to-t from-primaryLight to-white",
            isClosing ? "animate-scaleFadeOut" : "animate-scaleFadeIn",
            className
          )}
          {...props}
          role="dialog"
        >
          {withCloseIcon && (
            <button
              aria-label={t("basket.back")}
              className="
                size-8 grid place-items-center
                border-2 border-primaryOrange
                bg-white text-primaryOrange rounded-sm
                absolute right-3 top-3 z-10
                transition duration-150 cursor-pointer
                hover:bg-primaryOrange hover:text-white hover:scale-110 active:scale-95
              "
              onClick={handleClose}
              ref={closeIconRef}
            >
              <CloseIcon data-id={closeButtonDataId} size="default" />
            </button>
          )}
          <div className="max-h-[calc(100dvh-2rem-4px)] sm:max-h-[80dvh] px-4 overflow-auto fancy-scrollbars">
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

Modal.propTypes = {
  ariaLabel: string.isRequired,
  children: node.isRequired,
  className: string,
  closeButtonDataId: string,
  disableBackdropClick: bool,
  disableEscapePress: bool,
  doNotRenderInvisible: bool,
  firstElement: shape({ current: any }),
  isOpen: bool.isRequired,
  lastElement: shape({ current: any }),
  onClose: getConditionallyRequiredProp(func, props => props.withCloseIcon),
  preventGoBack: bool,
  withCloseIcon: bool,
};

export default Modal;
